import { useState } from 'react'
import { usePermission } from '@/features/auth/store'
import { useAnnexes, useCreateAnnexDraft } from './hooks'
import { CreateAnnexSheet } from './components/CreateAnnexSheet'
import type { Annex } from './types'

/**
 * Phase 5 entry point for the annex workflow: lists existing annexes (read), exposes
 * "Utwórz aneks" / "Kontynuuj draft" actions, and mounts {@link CreateAnnexSheet} for
 * drafting. Phase 6 will flesh this out with banners, field-level markers and audit-log
 * grouping; here we ship the minimum required to take a draft from create to publish.
 */
export function AnnexesTab({
  contractId,
  contractVersion,
  contractExtras,
}: {
  contractId: number
  contractVersion: number
  contractExtras: Record<string, unknown>
}) {
  const canWrite = usePermission('contracts.annex.write')
  const annexesQ = useAnnexes(contractId, false)
  const createDraft = useCreateAnnexDraft(contractId)
  const [openAnnexId, setOpenAnnexId] = useState<number | null>(null)

  const annexes = annexesQ.data ?? []
  const activeDraft = annexes.find(a => a.status === 'DRAFT') ?? null
  const published = annexes.filter(a => a.status === 'PUBLISHED')

  const handleCreate = () => {
    createDraft.mutate(undefined, {
      onSuccess: a => setOpenAnnexId(a.id),
    })
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Aneksy umowy</h2>
        {canWrite && (
          activeDraft ? (
            <button
              type="button"
              onClick={() => setOpenAnnexId(activeDraft.id)}
              className="px-3 py-1.5 text-sm rounded border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
            >
              Kontynuuj draft
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCreate}
              disabled={createDraft.isPending}
              className="px-3 py-1.5 text-sm rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {createDraft.isPending ? 'Tworzę draft...' : '+ Utwórz aneks'}
            </button>
          )
        )}
      </div>

      {annexesQ.isLoading && <p className="text-sm text-slate-500">Ładowanie...</p>}
      {annexesQ.error && (
        <p className="text-sm text-red-600">Błąd: {String(annexesQ.error)}</p>
      )}

      {activeDraft && (
        <div className="mb-4 border border-amber-200 bg-amber-50/60 rounded p-3">
          <div className="text-sm font-medium text-amber-900">
            Aneks w przygotowaniu (draft)
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            Autor {activeDraft.createdByName ?? `#${activeDraft.createdByUserId}`} · utworzony {fmtDate(activeDraft.createdAt)}
          </div>
        </div>
      )}

      {published.length === 0 && !activeDraft && !annexesQ.isLoading ? (
        <p className="text-sm text-slate-500">Brak opublikowanych aneksów.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {published.map(a => (
            <PublishedAnnexCard key={a.id} annex={a} />
          ))}
        </ul>
      )}

      {openAnnexId !== null && (
        <CreateAnnexSheet
          contractId={contractId}
          contractVersion={contractVersion}
          contractExtras={contractExtras}
          annexId={openAnnexId}
          onClose={() => setOpenAnnexId(null)}
        />
      )}
    </div>
  )
}

function PublishedAnnexCard({ annex }: { annex: Annex }) {
  return (
    <li className="border rounded p-3 bg-white">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-sm font-semibold">Aneks #{annex.annexNumber}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            Opublikowany {annex.publishedAt ? fmtDate(annex.publishedAt) : '—'} · autor {annex.createdByName ?? `#${annex.createdByUserId}`}
          </div>
        </div>
      </div>
      {annex.reason && (
        <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{annex.reason}</p>
      )}
      {(annex.changes ?? []).length > 0 && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">
            Zmiany w umowie ({(annex.changes ?? []).length})
          </div>
          <ul className="flex flex-col divide-y divide-slate-100 border rounded overflow-hidden">
            {(annex.changes ?? []).map((c, idx) => (
              <li
                key={`${c.fieldCode}-${idx}`}
                className="px-3 py-2 grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-sm"
              >
                <div className="min-w-0">
                  <div className="text-xs text-slate-500 uppercase tracking-wide truncate">
                    {c.sectionCode} · {c.fieldCode}
                  </div>
                  <div className="text-slate-500 line-through truncate">{fmtCellValue(c.oldValue)}</div>
                </div>
                <span className="text-slate-400 text-xs">→</span>
                <div className="text-slate-900 font-medium truncate">{fmtCellValue(c.newValue)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  )
}

function fmtCellValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Tak' : 'Nie'
  if (Array.isArray(v)) return v.join(', ')
  return String(v)
}

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })
}
